import React, { useEffect, useMemo, useState } from "react";
import {
  Plus, ArrowLeft, Save, AlertCircle, ListTree,
} from "lucide-react";
import { toast } from "sonner";
import { DragDropContext } from "@hello-pangea/dnd";
import PageHeader from "@/components/layout/PageHeader";
import { FormSection, FormField, StickyActionBar } from "@/components/common/FormSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { DECISION_LABELS } from "@/pages/hotline/constants";
import ProtocolTreeView from "@/pages/protocols/ProtocolNodeCard";

const EMPTY_META = { code: "", name: "", category_code: "", description: "", status: "active" };

function makeId() {
  return `n_${Math.random().toString(36).slice(2, 8)}`;
}

function indexNodes(nodes) {
  const byParent = new Map();
  for (const n of nodes) {
    const key = `${n.parent_id || "ROOT"}::${n.branch || "ROOT"}`;
    const arr = byParent.get(key) || [];
    arr.push(n);
    byParent.set(key, arr);
  }
  for (const arr of byParent.values()) arr.sort((a, b) => (a.order || 0) - (b.order || 0));
  return byParent;
}

function childrenOf(byParent, parentId, branch) {
  const key = `${parentId || "ROOT"}::${branch || "ROOT"}`;
  return byParent.get(key) || [];
}

export default function ProtocolEditor({ protocolId, onBack }) {
  const [meta, setMeta] = useState(EMPTY_META);
  const [nodes, setNodes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!protocolId);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const cats = await api.get("/lookups/categories", { params: { per_page: 200 } });
        const pc = (cats.data.data ?? []).find((c) => c.code === "protocol_categories");
        if (pc) {
          const r = await api.get("/lookups/values", { params: { category_id: pc.id } });
          setCategories(r.data.data ?? []);
        }
      } catch {}
      if (protocolId) {
        try {
          const r = await api.get(`/protocols/${protocolId}`);
          const protocol = r.data.data ?? {};
          setMeta({
            code: protocol.code, name: protocol.name,
            category_code: protocol.category_code || "",
            description: protocol.description || "",
            status: protocol.status,
          });
          setNodes(protocol.nodes || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
      }
    })();
  }, [protocolId]);

  const byParent = useMemo(() => indexNodes(nodes), [nodes]);
  const roots = useMemo(() => childrenOf(byParent, null, null), [byParent]);

  function addNode(parentId, branch) {
    const id = makeId();
    const siblings = childrenOf(byParent, parentId, branch);
    const newNode = {
      id, text: "", kind: "question",
      parent_id: parentId, branch,
      order: siblings.length, suggested_decision: null,
    };
    setNodes((prev) => [...prev, newNode]);
    setEditing(newNode);
    setEditForm(newNode);
  }

  function deleteNode(node) {
    const toDelete = new Set([node.id]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const n of nodes) {
        if (toDelete.has(n.parent_id) && !toDelete.has(n.id)) {
          toDelete.add(n.id);
          changed = true;
        }
      }
    }
    setNodes((prev) => prev.filter((n) => !toDelete.has(n.id)));
  }

  function saveEditing() {
    const text = (editForm.text || "").trim();
    if (!text) { toast.error("Node text is required"); return; }
    setNodes((prev) => prev.map((n) => n.id === editing.id ? { ...n, ...editForm, text } : n));
    setEditing(null);
  }

  function onDragEnd(result) {
    if (!result.destination) return;
    if (result.source.droppableId !== result.destination.droppableId) return;
    const [, parentId, branch] = result.source.droppableId.split(":");
    const siblings = childrenOf(byParent, parentId === "ROOT" ? null : parentId, branch === "ROOT" ? null : branch);
    const moved = siblings[result.source.index];
    const reordered = [...siblings];
    reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setNodes((prev) => prev.map((n) => {
      const idx = reordered.findIndex((r) => r.id === n.id);
      return idx >= 0 ? { ...n, order: idx } : n;
    }));
  }

  async function save() {
    const e = {};
    if (!meta.name) e.name = "Required";
    if (!meta.code) e.code = "Required";
    if (nodes.length === 0) e._nodes = "Add at least one node";
    setErrors(e);
    if (Object.keys(e).length) { toast.error("Fix validation errors"); return; }
    setSaving(true);
    try {
      const payload = { ...meta, nodes };
      if (protocolId) {
        const u = { ...payload };
        delete u.code;
        await api.put(`/protocols/${protocolId}`, u);
        toast.success("Protocol saved");
        onBack();
      } else {
        const r = await api.post("/protocols", payload);
        toast.success("Protocol created");
        onBack(r.data.id);
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  }

  if (loading) return <div className="p-6 text-sm text-[#6B7280]">Loading…</div>;

  return (
    <div data-testid="protocol-editor">
      <PageHeader
        title={protocolId ? "Edit Protocol" : "New Protocol"}
        subtitle="Define a decision tree of questions and recommendations. Drag to reorder siblings."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onBack()} className="border-[#E5E7EB]" data-testid="protocol-back">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
            </Button>
            <Button onClick={save} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="protocol-save">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        }
      />

      <div className="hah-card p-5 mb-4">
        <FormSection title="Protocol metadata" testId="protocol-meta">
          <FormField label="Code" required error={errors.code} hint="Unique slug. Cannot change after creation.">
            <Input data-testid="protocol-code" disabled={!!protocolId} value={meta.code} onChange={(e) => setMeta({ ...meta, code: e.target.value })} placeholder="e.g. cardiac_chest_pain_v1" />
          </FormField>
          <FormField label="Name" required error={errors.name}>
            <Input data-testid="protocol-name" value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} />
          </FormField>
          <FormField label="Category">
            <Select value={meta.category_code || ""} onValueChange={(v) => setMeta({ ...meta, category_code: v })}>
              <SelectTrigger data-testid="protocol-category"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c.id} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Status">
            <Select value={meta.status} onValueChange={(v) => setMeta({ ...meta, status: v })}>
              <SelectTrigger data-testid="protocol-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Description" full>
            <Textarea rows={2} value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} />
          </FormField>
        </FormSection>
      </div>

      <div className="hah-card p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="hah-section-title">Decision tree</div>
            <h3 className="text-base font-semibold text-[#1F2937] mt-0.5">Question & recommendation flow</h3>
            <p className="text-sm text-[#6B7280] mt-1">Build the protocol top-down. Each question branches by Yes / No / N/A.</p>
          </div>
          {roots.length === 0 && (
            <Button onClick={() => addNode(null, null)} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="protocol-add-root">
              <Plus className="w-4 h-4 mr-1.5" /> Add root question
            </Button>
          )}
        </div>

        {errors._nodes && (
          <div className="mb-3 p-3 rounded bg-[#FEF3C7] border border-[#FBBF24] text-[#92400E] text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {errors._nodes}
          </div>
        )}

        {roots.length === 0 ? (
          <div className="text-center py-10 text-sm text-[#6B7280] border-2 border-dashed border-[#E5E7EB] rounded-lg">
            <ListTree className="w-8 h-8 mx-auto text-[#94A3B8] mb-2" />
            No nodes yet. Add a root question to begin.
          </div>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <ProtocolTreeView roots={roots} byParent={byParent} onEdit={(n) => { setEditing(n); setEditForm(n); }} onDelete={deleteNode} onAddChild={addNode} />
          </DragDropContext>
        )}

        {roots.length > 0 && (
          <div className="mt-3">
            <Button onClick={() => addNode(null, null)} variant="outline" size="sm" className="border-[#E5E7EB]" data-testid="protocol-add-root-extra">
              <Plus className="w-4 h-4 mr-1.5" /> Add another root question
            </Button>
          </div>
        )}
      </div>

      <Sheet open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-[520px] overflow-y-auto p-0 bg-[#FAFBFC]">
          <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
            <SheetTitle>Edit node</SheetTitle>
            <SheetDescription>Configure question text or convert to a terminal recommendation.</SheetDescription>
          </SheetHeader>
          {editing && (
            <div className="p-6">
              <FormSection title="Node">
                <FormField label="Type">
                  <Select value={editForm.kind} onValueChange={(v) => setEditForm({ ...editForm, kind: v })}>
                    <SelectTrigger data-testid="node-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="question">Question</SelectItem>
                      <SelectItem value="recommendation">Recommendation</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label={editForm.kind === "recommendation" ? "Recommendation text" : "Question text"} full required>
                  <Textarea rows={3} value={editForm.text || ""} onChange={(e) => setEditForm({ ...editForm, text: e.target.value })} data-testid="node-text" />
                </FormField>
                {editForm.kind === "recommendation" && (
                  <FormField label="Suggested decision" full hint="Pre-fills the nurse's decision capture form.">
                    <Select value={editForm.suggested_decision || ""} onValueChange={(v) => setEditForm({ ...editForm, suggested_decision: v })}>
                      <SelectTrigger data-testid="node-suggested"><SelectValue placeholder="Select (optional)" /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(DECISION_LABELS).map(([k, label]) => (
                          <SelectItem key={k} value={k}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                )}
              </FormSection>
            </div>
          )}
          <StickyActionBar>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={saveEditing} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="node-save">Save node</Button>
          </StickyActionBar>
        </SheetContent>
      </Sheet>
    </div>
  );
}
